import { useState, useEffect } from 'react';
import { createHighlightedHTML } from '../utils/general';

export const useSearchHighlighting = (message, messages, searchQuery, isActiveSearchResult) => {
  const [highlightedContent, setHighlightedContent] = useState('');
  const [directSearchMatch, setDirectSearchMatch] = useState(false);

  // Listen for direct search result events
  useEffect(() => {
    const handleSearchResult = (e) => {
      const { query, currentResult, allResults } = e.detail;
      if (!query || !message?.text) return;

      // Check if this message is mentioned in the results by ID
      const isInResults = allResults.some(result => 
        result.messageId === message._id || 
        result.messageIndex === messages?.findIndex(m => m._id === message._id)
      );
      
      if (isInResults) {
        console.log('Direct search match for message:', message._id);
        
        // Double-check that the message actually contains the search term
        const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const escapedQuery = escapeRegExp(query);
        const regex = new RegExp(`(${escapedQuery})`, 'gi');
        
        if (regex.test(message.text)) {
          setDirectSearchMatch(true);
          
          // Check if this is the current active result
          const isActiveResult = currentResult.messageId === message._id || 
            currentResult.messageIndex === messages?.findIndex(m => m._id === message._id);
          
          // Force highlight generation
          const html = createHighlightedHTML(message.text, query, isActiveResult);
          
          if (html !== message.text) {
            console.log('Setting highlighted content via event for message', message._id);
            setHighlightedContent(html);
          }
        } else {
          console.log('Search result reported for this message but regex test failed');
          setDirectSearchMatch(false);
          setHighlightedContent('');
        }
      } else if (directSearchMatch) {
        // This message was previously matched but is no longer in results
        setDirectSearchMatch(false);
        setHighlightedContent('');
      }
    };
    
    const handleSearchClear = () => {
      if (directSearchMatch) {
        console.log('Clearing search highlight for message:', message._id);
        setDirectSearchMatch(false);
        setHighlightedContent('');
      }
    };

    window.addEventListener('search-result', handleSearchResult);
    window.addEventListener('search-clear', handleSearchClear);
    
    return () => {
      window.removeEventListener('search-result', handleSearchResult);
      window.removeEventListener('search-clear', handleSearchClear);
    };
  }, [message?._id, message?.text, messages, directSearchMatch]);

  // Generate highlighted content when search query changes
  useEffect(() => {
    if (message?.text && searchQuery?.trim()) {
      console.log('-------------------------------------------');
      console.log('HIGHLIGHT DEBUG - Message ID:', message._id);
      console.log('Search query:', searchQuery);
      console.log('Is active result:', isActiveSearchResult);
      console.log('Message text preview:', message.text.substring(0, 50));
      
      // Test if this message actually contains the search term
      const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedQuery = escapeRegExp(searchQuery);
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      const hasMatch = regex.test(message.text);
      
      console.log('Contains match using regex:', hasMatch);
      
      if (hasMatch) {
        const html = createHighlightedHTML(message.text, searchQuery, isActiveSearchResult);
        const hasHighlights = html !== message.text;
        
        console.log('HTML different from original:', hasHighlights);
        if (hasHighlights) {
          console.log('HTML sample:', html.substring(0, 200));
          
          // Force a re-render by ensuring we set a new value
          setHighlightedContent(prev => {
            if (html === prev) return html + ' '; // Force update if content didn't change
            return html;
          });
        }
      } else {
        // Clear highlights if no match in this message
        setHighlightedContent('');
      }
    } else if (!directSearchMatch) { // Don't clear if direct search match is active
      setHighlightedContent('');
    }
  }, [message?.text, searchQuery, isActiveSearchResult, directSearchMatch]);

  return {
    highlightedContent,
    directSearchMatch,
    hasHighlights: Boolean(highlightedContent)
  };
}; 